import React, { useCallback, useState } from 'react'
import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types'
import { IEcommerceAnswerSchema, IEcommerceProductGallery } from '../../types'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline'
import { getBigAssetPath } from '@aytm/lib/common/assets'
import style from './ZoomableAsset.module.scss'

interface IZoomableAssetProps {
  asset: IImageAsset | IEcommerceProductGallery
  answer: IEcommerceAnswerSchema
  assetIndex: number
}

const ZoomableAsset = ({ asset, answer, assetIndex }: IZoomableAssetProps) => {
  const [position, setPosition] = useState<null | { x: number; y: number }>(null)
  const [onImage, setOnImage] = useState<boolean>(false)
  const timeline = useTimeline()

  const onMoveOnImage = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onImage) return
      const element = event.currentTarget
      requestAnimationFrame(() => {
        const { left, top, width, height } = element.getBoundingClientRect()
        const centerX = width / 2
        const centerY = height / 2
        const mouseX = event.clientX - left
        const mouseY = event.clientY - top

        setPosition({
          x: -(mouseX - centerX) * 0.75,
          y: -(mouseY - centerY) * 0.75
        })
      })
    },
    [onImage]
  )

  const onMouseEnter = () => {
    if (!onImage) {
      timeline.log({ ref: answer.id, target: assetIndex.toString(), type: 'zoom_image_in' })
      setOnImage(true)
    }
  }

  const onMouseLeave = () => {
    requestAnimationFrame(() => {
      if (onImage) {
        timeline.log({ ref: answer.id, target: assetIndex.toString(), type: 'zoom_image_out' })
      }
      setOnImage(false)
      setPosition({ x: 0, y: 0 })
    })
  }

  return (
    <div
      className={`${style.imageWrapper} ${onImage ? style.imageWrapperActive : ''}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div
        className={style.image}
        style={{
          backgroundImage: `url(${getBigAssetPath(asset)})`,
          backgroundPosition: position ? `calc(50% + ${position.x}px) calc(50% + ${position.y}px)` : '50% 50%',
          transform: `scale(${onImage ? 2 : 1})`,
          cursor: onImage ? 'zoom-out' : 'zoom-in'
        }}
        onMouseMove={onMoveOnImage}
      />
    </div>
  )
}

export default ZoomableAsset
